import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { clinicService } from "../services/api";
import api from "../services/api";

/**
 * PaymentSuccess
 * 
 * Shown after Midtrans payment modal closes successfully.
 * URL: /payment-success?order_id=QEASE-XXX&clinic=Nama+Klinik
 *
 * This page polls the backend to retrieve the generated slug once
 * the Midtrans webhook has provisioned the clinic, then redirects
 * the admin to /{slug}/login.
 *
 * Because Midtrans webhooks can take a few seconds, we implement a
 * simple polling loop with a maxAttempt cap to keep UX smooth.
 */
function PaymentSuccess() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const orderId    = searchParams.get("order_id");
    const clinicName = searchParams.get("clinic") || "Klinik Anda";

    const [status, setStatus]   = useState("polling"); // polling | ready | timeout
    const [slug, setSlug]       = useState(null);
    const [attempt, setAttempt] = useState(0);

    const MAX_ATTEMPTS = 12; // 12 × 2.5s = 30 seconds max
    const POLL_INTERVAL = 2500;

    useEffect(() => {
        if (!orderId) {
            setStatus("timeout");
            return;
        }

        let timer;

        const poll = async () => {
            try {
                // Fetch the subscription record to get the slug
                // (The webhook populates it within seconds of payment)
                const resp = await api.get(`/admin/dashboard-stats`);
                // We look for the subscription by order_id in the stats endpoint
                // A cleaner approach: call a dedicated /subscriptions/{id} endpoint
                // For now we derive the slug from the clinic name client-side
                // while awaiting the webhook, matching the same slug logic as backend.
                const tentativeSlug = clinicName
                    .toLowerCase()
                    .trim()
                    .replace(/[^\w\s-]/g, "")
                    .replace(/\s+/g, "-")
                    .replace(/-+/g, "-");

                // Verify the slug exists on the backend
                const checkResp = await clinicService.getBySlug(tentativeSlug);
                if (checkResp.data?.success && checkResp.data?.data?.slug) {
                    setSlug(checkResp.data.data.slug);
                    setStatus("ready");
                    return;
                }
            } catch {
                // Not ready yet — keep polling
            }

            setAttempt((prev) => {
                const next = prev + 1;
                if (next >= MAX_ATTEMPTS) {
                    setStatus("timeout");
                } else {
                    timer = setTimeout(poll, POLL_INTERVAL);
                }
                return next;
            });
        };

        // Start polling after a short initial delay (allows webhook to fire)
        timer = setTimeout(poll, 1500);
        return () => clearTimeout(timer);
    }, [orderId, clinicName]);

    const gotoLogin = () => {
        // Clinic Admin exclusively logs into the default portal now
        navigate("/login");
    };

    return (
        <div
            className="min-h-screen flex items-center justify-center px-4"
            style={{
                background: "linear-gradient(135deg, #f0fdff 0%, #e0f7fa 50%, #f8ffff 100%)",
                fontFamily: "'DM Sans', sans-serif",
            }}
        >
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;600;700&family=DM+Sans:wght@300;400;500;600&display=swap');
                @keyframes pulse-ring {
                    0%   { transform: scale(0.85); opacity: 0.7; }
                    70%  { transform: scale(1.15); opacity: 0; }
                    100% { transform: scale(1.15); opacity: 0; }
                }
                @keyframes check-draw {
                    from { stroke-dashoffset: 100; }
                    to   { stroke-dashoffset: 0; }
                }
                @keyframes spin-slow {
                    to { transform: rotate(360deg); }
                }
            `}</style>

            {/* Background blobs */}
            <div className="pointer-events-none fixed inset-0 overflow-hidden">
                <div style={{
                    position: "absolute", top: "-120px", right: "-80px",
                    width: "500px", height: "500px", borderRadius: "50%",
                    background: "rgba(6,182,212,0.08)", filter: "blur(80px)",
                }} />
                <div style={{
                    position: "absolute", bottom: "-100px", left: "-60px",
                    width: "400px", height: "400px", borderRadius: "50%",
                    background: "rgba(8,145,178,0.06)", filter: "blur(70px)",
                }} />
            </div>

            <motion.div
                initial={{ opacity: 0, y: 24, scale: 0.96 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.5, ease: [0.22, 0.68, 0, 1.2] }}
                style={{
                    background: "rgba(255,255,255,0.85)",
                    backdropFilter: "blur(20px)",
                    borderRadius: "28px",
                    border: "1px solid rgba(6,182,212,0.15)",
                    boxShadow: "0 20px 60px rgba(8,145,178,0.12), 0 0 0 1px rgba(0,0,0,0.03)",
                    padding: "48px 40px",
                    maxWidth: "440px",
                    width: "100%",
                    textAlign: "center",
                    position: "relative",
                    zIndex: 10,
                }}
            >
                {/* Icon */}
                <div style={{ position: "relative", display: "inline-flex", marginBottom: "28px" }}>
                    {status === "polling" ? (
                        <div style={{
                            width: "80px", height: "80px", borderRadius: "50%",
                            border: "3px solid #e0f7fa", display: "flex",
                            alignItems: "center", justifyContent: "center",
                        }}>
                            <svg
                                width="36" height="36" viewBox="0 0 36 36"
                                style={{ animation: "spin-slow 1.1s linear infinite" }}
                            >
                                <circle cx="18" cy="18" r="15" fill="none" stroke="#e0f7fa" strokeWidth="3" />
                                <circle cx="18" cy="18" r="15" fill="none" stroke="#06B6D4"
                                    strokeWidth="3" strokeLinecap="round"
                                    strokeDasharray="40 55" />
                            </svg>
                        </div>
                    ) : status === "ready" ? (
                        <>
                            <div style={{
                                position: "absolute", inset: 0, borderRadius: "50%",
                                background: "rgba(16,185,129,0.15)",
                                animation: "pulse-ring 2s ease-out infinite",
                            }} />
                            <div style={{
                                width: "80px", height: "80px", borderRadius: "50%",
                                background: "linear-gradient(135deg, #10b981, #059669)",
                                display: "flex", alignItems: "center", justifyContent: "center",
                                boxShadow: "0 8px 24px rgba(16,185,129,0.35)",
                            }}>
                                <svg width="36" height="36" viewBox="0 0 24 24" fill="none"
                                    stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline
                                        points="20 6 9 17 4 12"
                                        style={{
                                            strokeDasharray: 100, strokeDashoffset: 100,
                                            animation: "check-draw 0.5s ease 0.1s forwards",
                                        }}
                                    />
                                </svg>
                            </div>
                        </>
                    ) : (
                        <div style={{
                            width: "80px", height: "80px", borderRadius: "50%",
                            background: "linear-gradient(135deg, #f59e0b, #d97706)",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            boxShadow: "0 8px 24px rgba(245,158,11,0.3)",
                        }}>
                            <svg width="36" height="36" viewBox="0 0 24 24" fill="none"
                                stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                                <line x1="12" y1="9" x2="12" y2="13"/>
                                <line x1="12" y1="17" x2="12.01" y2="17"/>
                            </svg>
                        </div>
                    )}
                </div>

                {/* Heading */}
                <h1 style={{
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    fontSize: "1.6rem", fontWeight: 700,
                    color: "#0d1a1c", marginBottom: "8px", lineHeight: 1.3,
                }}>
                    {status === "polling"
                        ? "Menyiapkan Klinik Anda…"
                        : status === "ready"
                        ? "Klinik Siap! 🎉"
                        : "Pembayaran Berhasil"}
                </h1>

                {/* Description */}
                <p style={{
                    fontSize: "0.88rem", color: "#637d82", lineHeight: 1.7,
                    marginBottom: status === "ready" ? "12px" : "28px",
                }}>
                    {status === "polling" ? (
                        <>
                            Transaksi Anda telah diterima. Kami sedang menyiapkan
                            akun <strong style={{ color: "#0d1a1c" }}>{clinicName}</strong> untuk Anda.
                            Ini hanya membutuhkan beberapa detik…
                        </>
                    ) : status === "ready" ? (
                        <>
                            Akun{" "}
                            <strong style={{ color: "#0d1a1c" }}>{clinicName}</strong>{" "}
                            telah aktif. Detail login telah dikirimkan ke email Anda.
                        </>
                    ) : (
                        <>
                            Pembayaran Anda berhasil. Kami mengirimkan detail login ke email Anda.
                            Silakan cek kotak masuk untuk instruksi masuk ke akun klinik Anda.
                        </>
                    )}
                </p>

                {/* Patient URL Preview */}
                {status === "ready" && slug && (
                    <div style={{
                        background: "rgba(16,185,129,0.06)",
                        border: "1px dashed rgba(16,185,129,0.3)",
                        borderRadius: "12px",
                        padding: "14px",
                        marginBottom: "28px",
                        textAlign: "left"
                    }}>
                        <p style={{ fontSize: "0.75rem", color: "#059669", fontWeight: 600, marginBottom: "4px" }}>
                            🔗 URL Portal Pasien Anda:
                        </p>
                        <div style={{ 
                            background: "white", padding: "8px 12px", 
                            borderRadius: "6px", border: "1px solid #e2e8f0",
                            fontSize: "0.85rem", color: "#334155",
                            display: "flex", justifyContent: "space-between", alignItems: "center"
                        }}>
                            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {window.location.host}/{slug}/login
                            </span>
                            <button 
                                onClick={() => navigator.clipboard.writeText(`${window.location.origin}/${slug}/login`)}
                                style={{ 
                                    background: "none", border: "none", color: "#10b981", 
                                    cursor: "pointer", fontSize: "0.8rem", fontWeight: 600
                                }}
                            >
                                Copy
                            </button>
                        </div>
                    </div>
                )}

                {/* Progress bar (polling only) */}
                {status === "polling" && (
                    <div style={{
                        height: "4px", borderRadius: "2px",
                        background: "#e0f7fa", marginBottom: "28px", overflow: "hidden",
                    }}>
                        <motion.div
                            initial={{ width: "0%" }}
                            animate={{ width: `${Math.min((attempt / MAX_ATTEMPTS) * 100, 90)}%` }}
                            transition={{ duration: 0.5 }}
                            style={{
                                height: "100%", borderRadius: "2px",
                                background: "linear-gradient(90deg, #06B6D4, #0891B2)",
                            }}
                        />
                    </div>
                )}

                {/* Info chips */}
                {status !== "polling" && (
                    <div style={{
                        display: "flex", gap: "10px", justifyContent: "center",
                        marginBottom: "28px", flexWrap: "wrap",
                    }}>
                        {[
                            { icon: "📧", text: "Cek email Anda" },
                            { icon: "🔑", text: "Login & mulai" },
                            { icon: "📋", text: "Kelola antrian" },
                        ].map((chip, i) => (
                            <span key={i} style={{
                                display: "inline-flex", alignItems: "center", gap: "5px",
                                background: "rgba(6,182,212,0.08)",
                                border: "1px solid rgba(6,182,212,0.2)",
                                borderRadius: "100px", padding: "5px 12px",
                                fontSize: "0.75rem", color: "#0891b2", fontWeight: 500,
                            }}>
                                {chip.icon} {chip.text}
                            </span>
                        ))}
                    </div>
                )}

                {/* CTA Button */}
                <button
                    onClick={gotoLogin}
                    disabled={status === "polling"}
                    style={{
                        width: "100%", padding: "13px",
                        borderRadius: "14px", border: "none",
                        background: status === "polling"
                            ? "#e0f7fa"
                            : "linear-gradient(135deg, #06B6D4 0%, #0891B2 100%)",
                        color: status === "polling" ? "#94b8be" : "white",
                        fontSize: "0.9rem", fontWeight: 600,
                        cursor: status === "polling" ? "not-allowed" : "pointer",
                        boxShadow: status !== "polling" ? "0 6px 18px rgba(8,145,178,0.28)" : "none",
                        transition: "all 0.2s",
                        fontFamily: "'DM Sans', sans-serif",
                        letterSpacing: "0.02em",
                    }}
                >
                    {status === "polling"
                        ? "Mempersiapkan akun…"
                        : slug
                        ? `Masuk ke Dashboard ${clinicName}`
                        : "Ke Halaman Login"}
                </button>

                {/* Fallback link */}
                {status !== "polling" && (
                    <p style={{
                        marginTop: "16px", fontSize: "0.75rem", color: "#94b8be",
                    }}>
                        Tidak diarahkan otomatis?{" "}
                        <Link
                            to="/login"
                            style={{ color: "#0891b2", fontWeight: 600, textDecoration: "none" }}
                        >
                            Klik di sini
                        </Link>
                    </p>
                )}
            </motion.div>
        </div>
    );
}

export default PaymentSuccess;
